import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Base64Tool: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');

  const encodeBase64 = () => {
    try {
      setError('');
      const encoded = btoa(input);
      setOutput(encoded);
    } catch (err) {
      setError('编码失败，请检查输入内容');
      setOutput('');
    }
  };

  const decodeBase64 = () => {
    try {
      setError('');
      const decoded = atob(input);
      setOutput(decoded);
    } catch (err) {
      setError('解码失败，请检查输入是否为有效的 Base64 编码');
      setOutput('');
    }
  };

  const handleConvert = () => {
    if (mode === 'encode') {
      encodeBase64();
    } else {
      decodeBase64();
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
  };

  const swapContent = () => {
    setInput(output);
    setOutput('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            返回首页
          </Link>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Base64 编解码</h1>
          <p className="text-gray-600">Base64 编码和解码工具</p>
        </div>

        {/* Mode Selection */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="encode"
                  checked={mode === 'encode'}
                  onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
                  className="mr-2"
                />
                <span className="text-gray-700">编码</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="decode"
                  checked={mode === 'decode'}
                  onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
                  className="mr-2"
                />
                <span className="text-gray-700">解码</span>
              </label>
            </div>
            <button
              onClick={handleConvert}
              className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-lg transition-colors"
            >
              {mode === 'encode' ? '编码' : '解码'}
            </button>
            <button
              onClick={clearAll}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              清空
            </button>
            <button
              onClick={swapContent}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              交换内容
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Input/Output */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {mode === 'encode' ? '输入文本' : '输入 Base64'}
            </h3>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={mode === 'encode' ? '请输入要编码的文本...' : '请输入要解码的 Base64 字符串...'}
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Output */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {mode === 'encode' ? 'Base64 编码结果' : '解码结果'}
              </h3>
              {output && (
                <button
                  onClick={copyToClipboard}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm transition-colors"
                >
                  复制
                </button>
              )}
            </div>
            <textarea
              value={output}
              readOnly
              placeholder={mode === 'encode' ? '编码后的结果将显示在这里...' : '解码后的结果将显示在这里...'}
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50"
            />
          </div>
        </div>

        {/* Examples */}
        <div className="mt-8 bg-purple-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">示例</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">编码示例</h4>
              <button
                onClick={() => {
                  setMode('encode');
                  setInput('Hello, World!');
                }}
                className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
              >
                加载编码示例
              </button>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-2">解码示例</h4>
              <button
                onClick={() => {
                  setMode('decode');
                  setInput('SGVsbG8sIFdvcmxkIQ==');
                }}
                className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
              >
                加载解码示例
              </button>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-2">关于 Base64</h4>
          <p className="text-gray-600 text-sm">
            Base64 是一种基于 64 个可打印字符来表示二进制数据的编码方法。
            常用于在 HTTP 协议中传输二进制数据，如邮件附件、图片等。
          </p>
        </div>
      </div>
    </div>
  );
};

export default Base64Tool;
